import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Bell, Mail, Settings, FileText, Calendar, CreditCard, Save, RefreshCw, Eye, AlertTriangle, Info, Smartphone } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import PushNotificationManager from '@/components/PushNotificationManager';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationSettings() {
  const [searchParams] = useSearchParams();
  const condominiumId = searchParams.get('condominium');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [preview, setPreview] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    fetchData();
  }, [token, condominiumId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const query = condominiumId ? `?condominium_id=${condominiumId}` : '';
      
      const [settingsRes, templatesRes, varsRes] = await Promise.all([
        fetch(`${API_URL}/api/notifications/settings${query}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/notifications/templates${query}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/notifications/variables`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data);
      }
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }
      if (varsRes.ok) {
        const data = await varsRes.json();
        setVariables(data.variables || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const query = condominiumId ? `?condominium_id=${condominiumId}` : '';
      const res = await fetch(`${API_URL}/api/notifications/settings${query}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success('Impostazioni salvate');
      } else {
        toast.error('Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async (template) => {
    try {
      const query = condominiumId ? `?condominium_id=${condominiumId}` : '';
      const res = await fetch(`${API_URL}/api/notifications/templates/${template.key}${query}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          subject_template: template.subject_template,
          body_template: template.body_template,
          active: template.active
        })
      });

      if (res.ok) {
        toast.success('Template salvato');
        fetchData();
      } else {
        toast.error('Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const resetTemplate = async (key) => {
    try {
      const query = condominiumId ? `?condominium_id=${condominiumId}` : '';
      const res = await fetch(`${API_URL}/api/notifications/templates/${key}/reset${query}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Template ripristinato');
        fetchData();
        setSelectedTemplate(null);
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const previewTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/templates/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          subject_template: selectedTemplate.subject_template,
          body_template: selectedTemplate.body_template
        })
      });

      if (res.ok) {
        const data = await res.json();
        setPreview(data);
      }
    } catch (error) {
      toast.error('Errore nel preview');
    }
  };

  const getTemplateLabel = (key) => {
    const labels = {
      PAYMENT_REMINDER: 'Promemoria Pagamento',
      OVERDUE_NOTICE: 'Sollecito Morosità',
      PAYMENT_RECEIVED: 'Conferma Pagamento',
      DOC_PUBLISHED: 'Documento Pubblicato',
      MEETING_NOTICE: 'Convocazione Assemblea',
      MEETING_REMINDER: 'Promemoria Assemblea',
      TRIAL_EXPIRING: 'Trial in Scadenza',
      PLAN_EXPIRED: 'Piano Scaduto',
      CUSTOM: 'Personalizzato'
    };
    return labels[key] || key;
  };

  const getTemplateIcon = (key) => {
    if (key.includes('PAYMENT') || key.includes('OVERDUE')) return <CreditCard className="h-4 w-4" />;
    if (key.includes('DOC')) return <FileText className="h-4 w-4" />;
    if (key.includes('MEETING')) return <Calendar className="h-4 w-4" />;
    if (key.includes('TRIAL') || key.includes('PLAN')) return <AlertTriangle className="h-4 w-4" />;
    return <Bell className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifiche"
        subtitle="Template e canali di invio"
        actions={
          <Button variant="outline" className="bg-white text-primary hover:bg-white/90" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
        }
      />

      {condominiumId && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Stai modificando le impostazioni per un condominio specifico. 
            Queste sovrascriveranno i valori predefiniti del workspace.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="push" className="w-full">
        <TabsList>
          <TabsTrigger value="push">
            <Smartphone className="h-4 w-4 mr-2" />
            Push
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-2" />
            Impostazioni
          </TabsTrigger>
          <TabsTrigger value="templates">
            <Mail className="h-4 w-4 mr-2" />
            Template
          </TabsTrigger>
        </TabsList>

        {/* Push Notifications Tab */}
        <TabsContent value="push" className="space-y-6">
          <PushNotificationManager />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Notifiche Pagamenti</CardTitle>
              <CardDescription>
                Promemoria automatici per rate in scadenza e morosità
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Promemoria scadenza</Label>
                  <p className="text-sm text-gray-500">
                    Invia promemoria prima della scadenza delle rate
                  </p>
                </div>
                <Switch
                  checked={settings?.payment_reminders_enabled ?? true}
                  onCheckedChange={(checked) => 
                    setSettings({...settings, payment_reminders_enabled: checked})
                  }
                />
              </div>
              
              {settings?.payment_reminders_enabled && (
                <div className="pl-4 border-l-2 space-y-2">
                  <Label className="text-sm">Giorni prima della scadenza</Label>
                  <div className="flex gap-2">
                    {[3, 7, 14].map(day => (
                      <Button
                        key={day}
                        size="sm"
                        variant={settings?.payment_reminder_days?.includes(day) ? "default" : "outline"}
                        onClick={() => {
                          const current = settings?.payment_reminder_days || [];
                          const newDays = current.includes(day) 
                            ? current.filter(d => d !== day)
                            : [...current, day].sort((a,b) => a-b);
                          setSettings({...settings, payment_reminder_days: newDays});
                        }}
                      >
                        {day} giorni
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label>Solleciti morosità</Label>
                  <p className="text-sm text-gray-500">
                    Invia solleciti per pagamenti scaduti
                  </p>
                </div>
                <Switch
                  checked={settings?.overdue_reminders_enabled ?? true}
                  onCheckedChange={(checked) => 
                    setSettings({...settings, overdue_reminders_enabled: checked})
                  }
                />
              </div>
              
              {settings?.overdue_reminders_enabled && (
                <div className="pl-4 border-l-2 space-y-2">
                  <Label className="text-sm">Giorni dopo la scadenza</Label>
                  <div className="flex gap-2">
                    {[1, 7, 14, 30].map(day => (
                      <Button
                        key={day}
                        size="sm"
                        variant={settings?.overdue_reminder_days?.includes(day) ? "default" : "outline"}
                        onClick={() => {
                          const current = settings?.overdue_reminder_days || [];
                          const newDays = current.includes(day) 
                            ? current.filter(d => d !== day)
                            : [...current, day].sort((a,b) => a-b);
                          setSettings({...settings, overdue_reminder_days: newDays});
                        }}
                      >
                        {day} {day === 1 ? 'giorno' : 'giorni'}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Altre Notifiche</CardTitle>
              <CardDescription>
                Documenti, assemblee e comunicazioni di sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-blue-500" />
                  <div>
                    <Label>Documenti pubblicati</Label>
                    <p className="text-sm text-gray-500">
                      Notifica quando vengono pubblicati nuovi documenti
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings?.document_notifications_enabled ?? true}
                  onCheckedChange={(checked) => 
                    setSettings({...settings, document_notifications_enabled: checked})
                  }
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-green-500" />
                  <div>
                    <Label>Convocazioni assemblee</Label>
                    <p className="text-sm text-gray-500">
                      Notifica e promemoria per assemblee condominiali
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings?.meeting_notifications_enabled ?? true}
                  onCheckedChange={(checked) => 
                    setSettings({...settings, meeting_notifications_enabled: checked})
                  }
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <div>
                    <Label>Notifiche sistema</Label>
                    <p className="text-sm text-gray-500">
                      Scadenza trial, piano scaduto, etc.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings?.saas_notifications_enabled ?? true}
                  onCheckedChange={(checked) => 
                    setSettings({...settings, saas_notifications_enabled: checked})
                  }
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={saveSettings} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Salvando...' : 'Salva Impostazioni'}
            </Button>
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Template List */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-sm">Template Disponibili</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {templates.map(template => (
                    <button
                      key={template.key}
                      onClick={() => setSelectedTemplate({...template})}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between ${
                        selectedTemplate?.key === template.key ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {getTemplateIcon(template.key)}
                        <div>
                          <p className="font-medium text-sm">{getTemplateLabel(template.key)}</p>
                          {template.is_default && (
                            <span className="text-xs text-gray-400">Predefinito</span>
                          )}
                        </div>
                      </div>
                      <Badge variant={template.active ? "default" : "secondary"} className="text-xs">
                        {template.active ? 'Attivo' : 'Disattivo'}
                      </Badge>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Template Editor */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm">
                  {selectedTemplate 
                    ? `Modifica: ${getTemplateLabel(selectedTemplate.key)}`
                    : 'Seleziona un template'
                  }
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedTemplate ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Attivo</Label>
                      <Switch
                        checked={selectedTemplate.active}
                        onCheckedChange={(checked) => 
                          setSelectedTemplate({...selectedTemplate, active: checked})
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Oggetto</Label>
                      <Input
                        value={selectedTemplate.subject_template}
                        onChange={(e) => 
                          setSelectedTemplate({...selectedTemplate, subject_template: e.target.value})
                        }
                        placeholder="Oggetto email..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Corpo messaggio</Label>
                      <Textarea
                        value={selectedTemplate.body_template}
                        onChange={(e) => 
                          setSelectedTemplate({...selectedTemplate, body_template: e.target.value})
                        }
                        rows={12}
                        className="font-mono text-sm"
                      />
                    </div>

                    {/* Variables Helper */}
                    <div className="border rounded-lg p-3 bg-gray-50">
                      <p className="text-xs font-medium mb-2">Variabili disponibili (clicca per inserire):</p>
                      <div className="flex flex-wrap gap-1">
                        {variables.slice(0, 15).map(v => (
                          <button
                            key={v.name}
                            onClick={() => {
                              const textarea = document.querySelector('textarea');
                              if (textarea) {
                                const pos = textarea.selectionStart;
                                const before = selectedTemplate.body_template.substring(0, pos);
                                const after = selectedTemplate.body_template.substring(pos);
                                setSelectedTemplate({
                                  ...selectedTemplate,
                                  body_template: `${before}{{${v.name}}}${after}`
                                });
                              }
                            }}
                            className="px-2 py-1 bg-white border rounded text-xs hover:bg-blue-50"
                            title={v.description}
                          >
                            {`{{${v.name}}}`}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={() => saveTemplate(selectedTemplate)}>
                        <Save className="h-4 w-4 mr-2" />
                        Salva
                      </Button>
                      <Button variant="outline" onClick={previewTemplate}>
                        <Eye className="h-4 w-4 mr-2" />
                        Anteprima
                      </Button>
                      {!selectedTemplate.is_default && (
                        <Button 
                          variant="ghost" 
                          onClick={() => resetTemplate(selectedTemplate.key)}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Ripristina
                        </Button>
                      )}
                    </div>

                    {/* Preview */}
                    {preview && (
                      <div className="border rounded-lg p-4 mt-4 bg-white">
                        <p className="text-xs font-medium text-gray-500 mb-2">ANTEPRIMA</p>
                        <p className="font-medium mb-2">{preview.subject}</p>
                        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-700">
                          {preview.body}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Seleziona un template dalla lista per modificarlo</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
